<script lang="ts">
  import { Plus } from 'lucide-svelte';
  import Modal from '../../ui/Modal.svelte';
  import { selectEntity } from '$lib/router';
  import { rcusQuery } from '$lib/queries/rcus';
  import { dinModulesForUnitQuery } from '$lib/queries/din_modules';
  import { loadsQuery } from '$lib/queries/loads';
  import { createRCUChannelMutation, deleteRCUChannelMutation, patchRCUChannelMutation } from '$lib/mutations/rcu_channels';
  import { patchDINChannelMutation, createDINChannelMutation } from '$lib/mutations/din_channels';
  import { rcuChannelsByRCUQuery, rcuChannelsQuery } from '$lib/queries/rcu_channels';
  import { dinChannelsQuery } from '$lib/queries/din_channels';
  import { createDinModuleMutation } from '$lib/mutations/din_modules';
  import { rcuCanvasUI } from '$lib/state/ui/rcuCanvas.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import { LOAD_TYPES } from '$lib/constants/load';
  import {
    DIN_MODULE_ADDRESS_RANGE,
    getNextAvailableDinAddress,
    getTakenDinAddresses
  } from '$lib/constants/din_module';
  import {
    DIN_MODULE_TYPES,
    LEGACY_DIN_MODULE_TYPES,
    dinModuleChannelCount,
    dinModuleLabel,
    channelOutputHint,
    loadCompatibleWithRow
  } from '$lib/hardware';

  const ALL_DIN_MODULE_TYPES = { ...DIN_MODULE_TYPES, ...LEGACY_DIN_MODULE_TYPES };
  import type { RCUResponse } from '$lib/types/rcu';
  import type { DinModuleResponse } from '$lib/types/din_module';
  import type { LoadResponse } from '$lib/types/load';
  import type { RCUChannelResponse } from '$lib/types/rcu_channel';
  import type { DINChannelResponse } from '$lib/types/din_channel';

  type GridRow = {
    channelNumber: number;
    channel: RCUChannelResponse | DINChannelResponse | null;
    load: LoadResponse | null;
    objectId: number;
    source: string;
    moduleType?: string;
  };

  let { type, id }: { type: 'rcu' | 'din'; id: number } = $props();

  const unitRcus = rcusQuery();
  const unitDinModules = dinModulesForUnitQuery();
  const unitLoads = loadsQuery();
  const createChannel = createRCUChannelMutation();
  const deleteRCUChannelMut = deleteRCUChannelMutation();
  const patchChannel = patchRCUChannelMutation();
  const patchDinChannel = patchDINChannelMutation();
  const createDinChannel = createDINChannelMutation();
  const rcuChannelList = rcuChannelsByRCUQuery(() => type === 'rcu' ? id : 0);
  const allRcuChannels = rcuChannelsQuery();
  const allDinChannels = dinChannelsQuery();

  let showUnassigned = $state(false);
  let assigningRow: GridRow | null = $state(null);
  let dinForm = $state({ module_type: 'phase_cut', address: 0 });

  const rcuList = $derived(unitRcus.data ?? []);
  const dinList = $derived(unitDinModules.data ?? []);
  const loadList = $derived(unitLoads.data ?? []);

  const rcu = $derived(type === 'rcu' ? rcuList.find((r) => r.id === id) : null);
  const din = $derived(type === 'din' ? dinList.find((d) => d.id === id) : null);
  const parentRcu = $derived(type === 'din' && din ? rcuList.find((r) => r.id === din.rcu) : rcu);

  let nextDinAddress = $derived(
    rcu ? getNextAvailableDinAddress(dinList, rcu.id, dinForm.module_type) : null
  );
  let takenDinAddresses = $derived(
    rcu ? getTakenDinAddresses(dinList, rcu.id, dinForm.module_type) : new Set<number>()
  );
  let canCreateDin = $derived(nextDinAddress != null);

  // Re-default the address whenever the modal opens or the selected module
  // type changes (nextDinAddress depends on dinForm.module_type). This only
  // clobbers dinForm.address when nextDinAddress itself actually changes, so
  // a manually-picked address for the current type is left alone.
  $effect(() => {
    if (rcuCanvasUI.showCreateRcuModal && nextDinAddress != null) {
      dinForm.address = nextDinAddress;
    }
  });

  const createDinMod = createDinModuleMutation(() => (type === 'rcu' ? id : 0));

  const dinChannelData = $derived(allDinChannels.data ?? []);
  const gridRows = $derived(buildGridRows(type, id, rcu, din, dinList, loadList, rcuChannelList.data ?? [], dinChannelData));

  const usedCount = $derived(gridRows.filter((row) => row.load).length);
  const totalCapacity = $derived(computeCapacity(type, rcu, din, dinList));
  const overCapacity = $derived(usedCount > totalCapacity);
  const unitRcuIds = $derived(new Set(rcuList.map((r) => r.id)));
  const unitDinModuleIds = $derived(new Set(dinList.map((d) => d.id)));
  const assignedLoadIds = $derived(
    new Set([
      ...(allRcuChannels.data ?? [])
        .filter((ch) => unitRcuIds.has(ch.rcu) && ch.load != null)
        .map((ch) => ch.load!),
      ...dinChannelData
        .filter((ch) => unitDinModuleIds.has(ch.din_module) && ch.load != null)
        .map((ch) => ch.load!)
    ])
  );
  const unassignedLoads = $derived(loadList.filter((l) => !assignedLoadIds.has(l.id)));
  const busy = $derived(
    patchChannel.isPending || patchDinChannel.isPending || createChannel.isPending || createDinChannel.isPending || deleteRCUChannelMut.isPending || createDinMod.isPending
  );

  const LOAD_TYPE_COLORS: Record<string, string> = {
    phase_cut: 'bg-amber-500/15',
    relay: 'bg-sky-500/15',
    '1-10v': 'bg-violet-500/15',
    curtain: 'bg-teal-500/15',
    'dali-dt6': 'bg-lime-500/15',
    'dali-dt8': 'bg-lime-500/15'
  };

  function computeCapacity(
    kind: 'rcu' | 'din',
    rcuEntity: RCUResponse | null | undefined,
    dinEntity: DinModuleResponse | null | undefined,
    allDin: DinModuleResponse[]
  ) {
    if (kind === 'din' && dinEntity) {
      return dinModuleChannelCount(dinEntity.module_type, dinEntity.dali_bus_count ?? 1);
    }
    if (kind === 'rcu' && rcuEntity) {
      const dinCap = allDin
        .filter((m) => m.rcu === rcuEntity.id)
        .reduce((sum, m) => sum + dinModuleChannelCount(m.module_type, m.dali_bus_count ?? 1), 0);
      return rcuEntity.channel_count + dinCap;
    }
    return 0;
  }

  function buildGridRows(
    kind: 'rcu' | 'din',
    entityId: number,
    rcuEntity: RCUResponse | null | undefined,
    dinEntity: DinModuleResponse | null | undefined,
    allDin: DinModuleResponse[],
    allLoads: LoadResponse[],
    rcuChannels: RCUChannelResponse[],
    dinChannels: DINChannelResponse[]
  ): GridRow[] {
    const loadById = (loadId: number) => allLoads.find((l) => l.id === loadId) ?? null;

    if (kind === 'din' && dinEntity) {
      const moduleChannels = dinChannels
        .filter((ch) => ch.din_module === entityId)
        .sort((a, b) => a.channel_number - b.channel_number);
      if (moduleChannels.length === 0) {
        const max = dinModuleChannelCount(dinEntity.module_type, dinEntity.dali_bus_count ?? 1);
        return Array.from({ length: max }, (_, i) => ({
          channelNumber: i + 1,
          channel: null,
          load: null,
          objectId: entityId,
          moduleType: dinEntity.module_type,
          source: `${dinModuleLabel(dinEntity.module_type)} @${dinEntity.address}`
        }));
      }
      return moduleChannels.map((ch) => ({
        channelNumber: ch.channel_number,
        channel: ch,
        load: ch.load ? loadById(ch.load) : null,
        objectId: entityId,
        moduleType: dinEntity.module_type,
        source: `${dinModuleLabel(dinEntity.module_type)} @${dinEntity.address}`
      }));
    }

    if (kind === 'rcu' && rcuEntity) {
      const rows: GridRow[] = [];
      for (const ch of rcuChannels) {
        rows.push({
          channelNumber: ch.channel_number,
          channel: ch,
          load: ch.load ? loadById(ch.load) : null,
          objectId: rcuEntity.id,
          source: 'RCU'
        });
      }
      const modules = allDin.filter((m) => m.rcu === rcuEntity.id);
      for (const mod of modules) {
        const moduleChannels = dinChannels
          .filter((ch) => ch.din_module === mod.id)
          .sort((a, b) => a.channel_number - b.channel_number);
        if (moduleChannels.length === 0) {
          const max = dinModuleChannelCount(mod.module_type, mod.dali_bus_count ?? 1);
          for (let ch = 1; ch <= max; ch++) {
            rows.push({
              channelNumber: ch,
              channel: null,
              load: null,
              objectId: mod.id,
              moduleType: mod.module_type,
              source: `${dinModuleLabel(mod.module_type)} @${mod.address}`
            });
          }
        } else {
          for (const ch of moduleChannels) {
            rows.push({
              channelNumber: ch.channel_number,
              channel: ch,
              load: ch.load ? loadById(ch.load) : null,
              objectId: mod.id,
              moduleType: mod.module_type,
              source: `${dinModuleLabel(mod.module_type)} @${mod.address}`
            });
          }
        }
      }
      return rows;
    }
    return [];
  }

  function loadsForRow(row: GridRow) {
    const pool = row.load ? [...unassignedLoads, row.load] : unassignedLoads;
    return pool.filter((load) => loadCompatibleWithRow(row, load));
  }

  async function assignLoadToRow(row: GridRow, loadId: string) {
    const load = loadId ? loadList.find((l) => l.id === Number(loadId)) : null;
    if (!row.objectId) return;
    try {
      if (row.channel) {
        if (row.source === 'RCU') {
          await patchChannel.mutateAsync({ id: row.channel.id, patch: { load: load?.id ?? null } });
        } else {
          await patchDinChannel.mutateAsync({ id: row.channel.id, patch: { load: load?.id ?? null } });
        }
      } else if (load) {
        if (row.source === 'RCU') {
          await createChannel.mutateAsync({ rcu: row.objectId, channel_number: row.channelNumber, load: load.id });
        } else {
          await createDinChannel.mutateAsync({ din_module: row.objectId, channel_number: row.channelNumber, load: load.id });
        }
      }
      toast.success(load ? 'Channel assigned' : 'Channel unassigned');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      assigningRow = null;
    }
  }

  async function createDinModule() {
    if (!rcu) return;
    if (nextDinAddress == null) {
      toast.error('No addresses available on this RCU for this module type (0–15).');
      return;
    }
    if (takenDinAddresses.has(dinForm.address)) {
      toast.error(
        `Address ${dinForm.address} is already used by a ${dinModuleLabel(dinForm.module_type)} on this RCU.`
      );
      return;
    }
    try {
      const newMod = await createDinMod.mutateAsync({
        rcu: rcu.id,
        module_type: dinForm.module_type as
          | 'phase_cut'
          | 'relay'
          | '1-10v'
          | 'curtain'
          | 'dali_master',
        address: dinForm.address,
        ...(dinForm.module_type === 'dali_master' && { dali_bus_count: 1 })
      });
      rcuCanvasUI.showCreateRcuModal = false;
      const next = getNextAvailableDinAddress([...dinList, newMod], rcu.id, 'phase_cut');
      dinForm = { module_type: 'phase_cut', address: next ?? 0 };
      toast.success('DIN module added');
      selectEntity('din', newMod.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }
</script>

<div class="p-3">
  <div class="flex items-start justify-between mb-3 gap-3">
    <div>
      <h2 class="text-base font-medium text-text-primary">
        {#if type === 'rcu'}
          {rcu?.name ?? `RCU ${id}`}
        {:else}
          {dinModuleLabel(din?.module_type ?? '') || 'DIN'} @
          <span class="font-mono">{din?.address ?? id}</span>
        {/if}
      </h2>
      {#if type === 'din' && parentRcu}
        <p class="text-xs text-text-muted">Under {parentRcu.name}</p>
      {/if}
    </div>

    {#if type === 'rcu'}
      <button
        type="button"
        class="btn-primary inline-flex items-center gap-1.5 text-sm shrink-0"
        onclick={() => (rcuCanvasUI.showCreateRcuModal = true)}
      >
        <Plus class="w-3.5 h-3.5" />
        Add DIN module
      </button>
    {/if}
  </div>

  <div class="mb-3 flex items-center gap-2 text-sm flex-wrap">
    <span class="text-text-muted">Capacity</span>
    <span class="font-mono font-medium {overCapacity ? 'text-state-error' : 'text-text-primary'}">
      {usedCount}/{totalCapacity}
    </span>
    <span class="text-text-muted">channels used</span>
    {#if overCapacity}
      <span class="text-xs text-state-error font-medium">Over capacity</span>
    {/if}
    <div class="flex-1 h-1.5 bg-bg-hover rounded overflow-hidden max-w-xs">
      <div
        class="h-full transition-all {overCapacity ? 'bg-state-error' : 'bg-accent-primary'}"
        style="width: {totalCapacity ? Math.min(100, (usedCount / totalCapacity) * 100) : 0}%"
      ></div>
    </div>
    <button
      type="button"
      class="text-xs text-text-secondary hover:text-text-primary border border-border-default rounded px-2 py-0.5"
      onclick={() => (showUnassigned = !showUnassigned)}
    >
      Unassigned ({unassignedLoads.length})
    </button>
  </div>

  <div class="flex gap-3">
    <div class="flex-1 min-w-0 border border-border-default rounded overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-bg-rail text-text-muted text-xs">
          <tr class="h-8">
            <th class="text-left px-3 font-medium w-16">Ch</th>
            <th class="text-left px-3 font-medium w-28">Source</th>
            <th class="text-left px-3 font-medium">Load</th>
            <th class="text-left px-3 font-medium w-32">Type</th>
            <th class="text-right px-3 font-medium w-20">W</th>
            <th class="text-left px-3 font-medium w-24">Output</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-border-default">
          {#each gridRows as row}
            <tr
              class="h-8 hover:bg-bg-hover {row.load
                ? (LOAD_TYPE_COLORS[row.load.load_type] ?? '')
                : ''}"
            >
              <td class="px-3 font-mono">{row.channelNumber}</td>
              <td class="px-3 text-xs text-text-secondary truncate max-w-[7rem]" title={row.source}>
                {row.source}
              </td>
              <td class="px-3">
                <select
                  class="w-full max-w-[12rem] h-7 text-xs border border-border-default rounded bg-bg-canvas truncate"
                  value={row.load?.id ?? ''}
                  disabled={busy}
                  onchange={(e) => assignLoadToRow(row, e.currentTarget.value)}
                >
                  <option value="">— Unassigned —</option>
                  {#each loadsForRow(row) as loadItem (loadItem.id)}
                    <option value={loadItem.id}>{loadItem.name}</option>
                  {/each}
                </select>
              </td>
              <td class="px-3 text-text-secondary text-xs">
                {row.load
                  ? (LOAD_TYPES[row.load.load_type as keyof typeof LOAD_TYPES]?.label ??
                    row.load.load_type)
                  : '—'}
              </td>
              <td class="px-3 text-right font-mono text-text-muted">
                {row.load?.total_wattage ?? row.load?.wattage_per_unit ?? '—'}
              </td>
              <td class="px-3 text-xs text-text-muted">{channelOutputHint(row, row.load)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    {#if showUnassigned}
      <aside class="w-44 shrink-0 border border-border-default rounded p-2 text-xs pop-in">
        <div class="font-medium text-text-primary mb-2">Unassigned loads</div>
        {#if unassignedLoads.length === 0}
          <p class="text-text-muted">All loads assigned</p>
        {:else}
          <ul class="space-y-1 max-h-64 overflow-y-auto">
            {#each unassignedLoads as loadItem (loadItem.id)}
              <li>
                <button
                  type="button"
                  class="w-full text-left truncate hover:text-accent-primary"
                  onclick={() => selectEntity('load', loadItem.id)}
                >
                  {loadItem.name}
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </aside>
    {/if}
  </div>
</div>

{#if rcu}
  <Modal
    open={rcuCanvasUI.showCreateRcuModal}
    size="md"
    onclose={() => (rcuCanvasUI.showCreateRcuModal = false)}
  >
    {#snippet header()}
      <h4 class="text-sm font-medium text-text-primary">Add DIN module</h4>
    {/snippet}
    {#snippet children()}
      <div class="space-y-3">
        <div>
          <label class="block text-2xs text-text-muted mb-1" for="mod-type">Module type</label>
          <select
            id="mod-type"
            bind:value={dinForm.module_type}
            class="w-full h-8 px-2 text-sm border border-border-strong rounded"
          >
            {#each Object.values(ALL_DIN_MODULE_TYPES) as cfg (cfg.key)}
              <option value={cfg.key}>{cfg.label}</option>
            {/each}
          </select>
        </div>
        <div>
          <label class="block text-2xs text-text-muted mb-1" for="mod-addr">Address</label>
          {#if nextDinAddress != null}
            <select
              id="mod-addr"
              bind:value={dinForm.address}
              class="w-full h-8 px-2 text-sm font-mono border border-border-strong rounded"
            >
              {#each DIN_MODULE_ADDRESS_RANGE as addr}
                <option value={addr} disabled={takenDinAddresses.has(addr)}>
                  {addr}{takenDinAddresses.has(addr) ? ' (in use)' : ''}
                </option>
              {/each}
            </select>
          {:else}
            <p class="text-sm text-state-error">No addresses available on this RCU (0–15).</p>
          {/if}
        </div>
      </div>
    {/snippet}
    {#snippet footer()}
      <div class="flex justify-end gap-2">
        <button
          type="button"
          class="px-3 py-1.5 text-sm border border-border-default rounded"
          onclick={() => (rcuCanvasUI.showCreateRcuModal = false)}
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn-primary text-sm"
          disabled={busy || !canCreateDin}
          onclick={createDinModule}
        >
          Create
        </button>
      </div>
    {/snippet}
  </Modal>
{/if}
